// First Created - 12 Jun 2026
// Author - Atul Pandey
// Purpose - To create an Org Trail Session Cohort / Batch for Trinity trail delivery

import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router"
import useTitle from "@/hooks/useTitle"
import EtBtnFormBasic from "@/components/common/buttons/EtBtnFormBasic"
import PulseLoader from "react-spinners/PulseLoader"

import { useGetOrganizationsQuery } from "@/pages/admin/organizations/organizationsApiSlice"
import { useGetTrailsQuery } from "@/features/trails/trailsApiSlice"
import { useGetUsersQuery } from "@/features/users/usersApiSlice"

import {
  useCreateNewOrgTrailSessionCohortMutation,
  useGetOrgTrailSessionCohortsQuery,
} from "./orgTrailSessionCohortsApiSlice"


const EtCreateOrgTrailSessionCohort = ({ setActiveAdminView }) => {
  // First Created - 12 Jun 2026
  // Author - Atul Pandey
  // Purpose - To create an Org Trail Session Cohort / Batch for Trinity trail delivery
  // Updated - 14 Jun 2026 - added cohortUserIds
  // Updated - 15 Jun 2026 - refinement for cohort creation / assignment based on user's initial assignments

  useTitle("Create Cohort")

  const navigate = useNavigate()

  const [
    createNewOrgTrailSessionCohort,
    { isLoading, isSuccess, isError, error },
  ] = useCreateNewOrgTrailSessionCohortMutation()

  const {
    data: organizations,
    isSuccess: isOrganizationsSuccess,
    } = useGetOrganizationsQuery("organizationsList", {
    pollingInterval: 60000,
    refetchOnFocus: true,
    refetchOnMountOrArgChange: true,
  })

  const {
    data: trails,
    isSuccess: isTrailsSuccess,
  } = useGetTrailsQuery("trailsList", {
    pollingInterval: 60000,
    refetchOnFocus: true,
    refetchOnMountOrArgChange: true,
  })

  const {
    data: users,
    isSuccess: isUsersSuccess,
  } = useGetUsersQuery("usersList", {
    pollingInterval: 60000,
    refetchOnFocus: true,
    refetchOnMountOrArgChange: true,
  })

  // 15 Jun 2026 - Existing cohorts used to display users by current cohort assignment
  const {
    data: existingCohorts,
    isSuccess: isExistingCohortsSuccess,
    } = useGetOrgTrailSessionCohortsQuery({
    active: true,
  })

  const initialFormState = {
    orgId: "",
    trailId: "",
    sessionId: "",
    cohortName: "",
    cohortCode: "",
    cohortDesc: "",
    cohortSequence: "",
    cohortStatus: "Planned",
    plannedStartDate: "",
    plannedEndDate: "",
    coachIds: [],
    sponsorIds: [],
    cohortUserIds: [], // 14 Jun 2026
    aboutMe: "",
    pic: "",
    active: true,
  }

  const [formData, setFormData] = useState(initialFormState)

  useEffect(() => {
    if (isSuccess) {
      setFormData(initialFormState)

      if (setActiveAdminView) {
        setActiveAdminView("orgTrailSessionCohorts")
      } else {
        navigate("/orgTrailSessionCohorts")
      }
    }
  }, [isSuccess])

  const handleChange = e => {
    const { name, value, files, type, checked } = e.target

    if (name === "pic") {
      setFormData(prevState => ({
        ...prevState,
        pic: files[0],
      }))
    } else if (type === "checkbox") {
      setFormData(prevState => ({
        ...prevState,
        [name]: checked,
      }))
    } else {
      setFormData(prevState => ({
        ...prevState,
        [name]: value,
        // 12 Jun 2026 - Org change should reset selected sponsors and cohortUsers
        ...(name === "orgId" ? { sponsorIds: [], cohortUserIds: [] } : {}),
        ...(name === "trailId" ? { cohortUserIds: [] } : {}), // 15 Jun 2026
      }))
    }
  }

  const handleCoachToggle = coachId => {
    const exists = formData.coachIds.some(
      item => item.coachId?.toString() === coachId?.toString()
    )

    const updatedCoachIds = exists
      ? formData.coachIds.filter(
          item => item.coachId?.toString() !== coachId?.toString()
        )
      : [...formData.coachIds, { coachId }]

    setFormData(prevState => ({
      ...prevState,
      coachIds: updatedCoachIds,
    }))
  }

  const handleSponsorToggle = sponsorId => {
    const exists = formData.sponsorIds.some(
      item => item.sponsorId?.toString() === sponsorId?.toString()
    )

    const updatedSponsorIds = exists
      ? formData.sponsorIds.filter(
          item => item.sponsorId?.toString() !== sponsorId?.toString()
        )
      : [...formData.sponsorIds, { sponsorId }]

    setFormData(prevState => ({
      ...prevState,
      sponsorIds: updatedSponsorIds,
    }))
  }

  // 14 Jun 2026
  // 15 Jun 2026 - Selected users will be transferred into the new cohort by backend
  const handleCohortUserToggle = userId => {
    const exists = formData.cohortUserIds.some(
        item => item.userId?.toString() === userId?.toString()
    )

    const updatedCohortUserIds = exists
        ? formData.cohortUserIds.filter(
            item => item.userId?.toString() !== userId?.toString()
        )
        : [
            ...formData.cohortUserIds,
            {
            userId,
            userCohortStatus: "Planned",
            active: true,
            },
        ]

    setFormData(prevState => ({
        ...prevState,
        cohortUserIds: updatedCohortUserIds,
    }))
  }

  const handleSubmit = async e => {
    e.preventDefault()

    const cohortData = {
      orgId: formData.orgId,
      trailId: formData.trailId,
      sessionId: formData.sessionId || undefined,
      cohortName: formData.cohortName,
      cohortCode: formData.cohortCode,
      cohortDesc: formData.cohortDesc,
      cohortSequence: formData.cohortSequence
        ? Number(formData.cohortSequence)
        : undefined,
      cohortStatus: formData.cohortStatus,
      plannedStartDate: formData.plannedStartDate || undefined,
      plannedEndDate: formData.plannedEndDate || undefined,
      coachIds: formData.coachIds,
      sponsorIds: formData.sponsorIds,
      cohortUserIds: formData.cohortUserIds, // 14 Jun 2026
      aboutMe: formData.aboutMe,
      active: formData.active,
    }

    if (formData.pic) {
      const reader = new FileReader()

      reader.onloadend = async () => {
        cohortData.pic = reader.result

        // 15 Jun 2026
        try {
            const response = await createNewOrgTrailSessionCohort(cohortData).unwrap()

            alert(
                `${response?.message || "Cohort created"}\n\n` +
                `Users added: ${response?.usersAdded || 0}\n` +
                `Users transferred: ${response?.usersTransferred || 0}`
            )
            } catch (error) {
            console.error(error)
            alert(error?.data?.message || "Failed to create cohort")
        }

        // try {
        //   await createNewOrgTrailSessionCohort(cohortData).unwrap()
        // } catch (error) {
        //   console.error(error)
        // }
      }

      reader.readAsDataURL(formData.pic)
    } else {
        // 15 Jun 2026
        try {
            const response = await createNewOrgTrailSessionCohort(cohortData).unwrap()

            alert(
                `${response?.message || "Cohort created"}\n\n` +
                `Users added: ${response?.usersAdded || 0}\n` +
                `Users transferred: ${response?.usersTransferred || 0}`
            )
            } catch (error) {
            console.error(error)
            alert(error?.data?.message || "Failed to create cohort")
        }
    //   try {
    //     await createNewOrgTrailSessionCohort(cohortData).unwrap()
    //   } catch (error) {
    //     console.error(error)
    //   }
    }
  }

  const handleCancel = () => {
    if (setActiveAdminView) {
      setActiveAdminView("orgTrailSessionCohorts")
    } else {
      navigate("/orgTrailSessionCohorts")
    }
  }

  const handleClear = () => {
    setFormData(initialFormState)
  }

  let organizationOptions = null
  let trailOptions = null
  let coachOptions = null
  let sponsorOptions = null
  let cohortUserOptions = null // 14 Jun 2026

  if (isOrganizationsSuccess) {
    const { ids, entities } = organizations

    organizationOptions = ids?.map(orgId => {
        const organization = entities[orgId]

        return (
        <option key={orgId} value={orgId}>
            {organization?.organizationName}
        </option>
        )
  })}

  if (isTrailsSuccess) {
    const { ids, entities } = trails

    trailOptions = ids?.map(trailId => {
      const trail = entities[trailId]

      return (
        <option key={trailId} value={trailId}>
          {trail?.trailName}
        </option>
      )
    })
  }

  // 12 Jun 2026 - Resolve id whether field is populated object or plain string
  const getRefId = value => {
    if (!value) return ""
    if (typeof value === "string") return value
    return value?._id || value?.id || ""
  }

  // 14 Jun 2026 - Resolve role display whether userOrgRole is string or populated object
  const getOrgRoleDisplay = value => {
    if (!value) return ""

    if (typeof value === "string") return value

    return value?.orgRoleName || value?.name || ""
  }

  // 12 Jun 2026 - Keep sponsor list scoped to selected org
  const isSameOrg = user => {
    const selectedOrgId = formData.orgId?.toString()
    const userOrgId = getRefId(user?.userOrgId)?.toString()

    if (!selectedOrgId || !userOrgId) return false

    return selectedOrgId === userOrgId
  }


  // 14 Jun 2026
  const isSponsorUser = user => {
    const orgRoleDisplay = getOrgRoleDisplay(user?.userOrgRole)

    return (
        user?.roles?.includes("OrgSponsor") ||
        user?.roles?.includes("Sponsor") ||
        orgRoleDisplay === "Sponsor" ||
        orgRoleDisplay === "OrgSponsor"
    )
  }

//   const isSponsorUser = user => {
//     return (
//         user?.roles?.includes("OrgSponsor") ||
//         user?.roles?.includes("Sponsor") ||
//         user?.userOrgRole === "Sponsor"
//     )
//   }

  // 14 Jun 2026
  const isCohortEligibleUser = user => {
    return (
        isSameOrg(user) &&
        user?.active !== false &&
        !user?.roles?.includes("EtAdmin") &&
        !user?.roles?.includes("EtCoach")
    )
  }

  // 15 Jun 2026 - Check if cohort belongs to selected org + trail
const isSelectedOrgTrailCohort = cohort => {
  const selectedOrgId = formData.orgId?.toString()
  const selectedTrailId = formData.trailId?.toString()

  const cohortOrgId = getRefId(cohort?.orgId)?.toString()
  const cohortTrailId = getRefId(cohort?.trailId)?.toString()

  return (
    selectedOrgId &&
    selectedTrailId &&
    cohortOrgId === selectedOrgId &&
    cohortTrailId === selectedTrailId &&
    cohort?.active !== false
  )
}

// 15 Jun 2026 - Resolve user's current cohort for selected org + trail
const getCurrentCohortForUser = userId => {
  if (!isExistingCohortsSuccess || !existingCohorts?.ids) return null

  const cohortList = existingCohorts.ids.map(id => existingCohorts.entities[id])

  return cohortList.find(cohort => {
    if (!isSelectedOrgTrailCohort(cohort)) return false

    return cohort?.cohortUserIds?.some(item => {
      const itemUserId = getRefId(item?.userId)?.toString()

      return itemUserId === userId?.toString()
    })
  })
}

// 15 Jun 2026 - Group eligible users under current cohort layers
const getCohortUserGroups = userList => {
  const eligibleUsers = userList.filter(user => isCohortEligibleUser(user))

  const groups = {}

  eligibleUsers.forEach(user => {
    const currentCohort = getCurrentCohortForUser(user.id)
    const groupId = currentCohort?._id || "unassigned"
    const groupName =
      currentCohort?.cohortName ||
      currentCohort?.cohortCode ||
      "Unassigned Users"

    if (!groups[groupId]) {
      groups[groupId] = {
        cohortId: groupId,
        cohortName: groupName,
        isDefaultCohort: currentCohort?.isDefaultCohort || false,
        users: [],
      }
    }

    groups[groupId].users.push({
      ...user,
      currentCohort,
    })
  })

  return Object.values(groups).sort((a, b) => {
    if (a.isDefaultCohort && !b.isDefaultCohort) return 1
    if (!a.isDefaultCohort && b.isDefaultCohort) return -1

    return (a.cohortName || "").localeCompare(b.cohortName || "")
  })
}

//   if (isUsersSuccess) {
//     const { ids, entities } = users

//     const userList = ids?.map(userId => entities[userId]) || []

//     const coachUsers = userList.filter(user =>
//       user?.roles?.includes("EtCoach")
//     )

//     const sponsorUsers = userList.filter(user =>
//       user?.roles?.includes("OrgSponsor") ||
//       user?.roles?.includes("Sponsor") ||
//       user?.userOrgRole === "Sponsor"
//     )

//     coachOptions = coachUsers.map(coach => {
//       const isChecked = formData.coachIds.some(
//         item => item.coachId?.toString() === coach.id?.toString()
//       )

//       return (
//         <div
//           key={coach.id}
//           className="flex justify-start items-center bg-gray-100 rounded m-1 p-1"
//         >
//           <input
//             type="checkbox"
//             checked={isChecked}
//             onChange={() => handleCoachToggle(coach.id)}
//             className="m-1"
//           />
//           <p className="text-xs m-1">
//             {coach?.name || coach?.username || coach?.email}
//           </p>
//         </div>
//       )
//     })

//     sponsorOptions = sponsorUsers.map(sponsor => {
//       const isChecked = formData.sponsorIds.some(
//         item => item.sponsorId?.toString() === sponsor.id?.toString()
//       )

//       return (
//         <div
//           key={sponsor.id}
//           className="flex justify-start items-center bg-gray-100 rounded m-1 p-1"
//         >
//           <input
//             type="checkbox"
//             checked={isChecked}
//             onChange={() => handleSponsorToggle(sponsor.id)}
//             className="m-1"
//           />
//           <p className="text-xs m-1">
//             {sponsor?.name || sponsor?.username || sponsor?.email}
//           </p>
//         </div>
//       )
//     })
//   }

if (isUsersSuccess) {
  const { ids, entities } = users

  const userList = ids?.map(userId => entities[userId]) || []

  const coachUsers = userList.filter(user =>
    user?.roles?.includes("EtCoach")
  )

  // 12 Jun 2026 - Sponsors should be limited to the selected org
  const sponsorUsers = userList.filter(user =>
    isSameOrg(user) && isSponsorUser(user)
  )

  // 14 Jun 2026 - Cohort users should be limited to the selected org
  const cohortUsers = userList.filter(user =>
    isCohortEligibleUser(user)
  )

  coachOptions = coachUsers.map(coach => {
    const isChecked = formData.coachIds.some(
      item => item.coachId?.toString() === coach.id?.toString()
    )

    return (
      <div
        key={coach.id}
        className="flex justify-start items-center bg-gray-100 rounded m-1 p-1"
      >
        <input
          type="checkbox"
          checked={isChecked}
          onChange={() => handleCoachToggle(coach.id)}
          className="m-1"
        />
        <p className="text-xs m-1">
          {coach?.name || coach?.username || coach?.email}
        </p>
      </div>
    )
  })

  sponsorOptions = sponsorUsers.map(sponsor => {
    const isChecked = formData.sponsorIds.some(
      item => item.sponsorId?.toString() === sponsor.id?.toString()
    )

    return (
      <div
        key={sponsor.id}
        className="flex justify-start items-center bg-gray-100 rounded m-1 p-1"
      >
        <input
          type="checkbox"
          checked={isChecked}
          onChange={() => handleSponsorToggle(sponsor.id)}
          className="m-1"
        />
        <p className="text-xs m-1">
          {sponsor?.name || sponsor?.username || sponsor?.email}
        </p>
      </div>
    )
  })

  // 15 Jun 2026 - Show cohort users grouped by current cohort assignment
  const cohortUserGroups = getCohortUserGroups(userList)

  cohortUserOptions = cohortUserGroups.map(group => {
    return (
        <div key={group.cohortId} className="bg-gray-50 rounded m-1 p-1">
        <div className="flex justify-between items-center bg-gray-200 rounded m-1 p-2">
            <p className="text-xs font-bold text-gray-700">
            {group.cohortName}
            </p>

            <p className="text-[10px] bg-white border border-orange-600 text-orange-600 rounded-full px-2 py-1">
            {group.users?.length || 0}
            </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
            {group.users.map(cohortUser => {
            const isChecked = formData.cohortUserIds.some(
                item => item.userId?.toString() === cohortUser.id?.toString()
            )

            return (
                <div
                key={cohortUser.id}
                className="flex justify-between items-center bg-white rounded m-1 p-1"
                >
                <div className="flex justify-start items-center">
                    <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => handleCohortUserToggle(cohortUser.id)}
                    className="m-1"
                    />

                    <div>
                    <p className="text-xs m-1">
                        {cohortUser?.name || cohortUser?.username || cohortUser?.email}
                    </p>

                    <p className="text-[9px] text-gray-500 mx-1">
                        {cohortUser?.email}
                    </p>
                    </div>
                </div>

                <div className="flex justify-end items-center flex-wrap">
                    {cohortUser?.currentCohort && (
                    <p className="text-[8px] bg-gray-100 text-gray-700 rounded-full mx-1 px-2 py-1">
                        Current: {cohortUser.currentCohort?.cohortCode || cohortUser.currentCohort?.cohortName}
                    </p>
                    )}

                    {getOrgRoleDisplay(cohortUser?.userOrgRole) && (
                    <p className="text-[8px] bg-orange-100 text-orange-700 rounded-full mx-1 px-2 py-1">
                        {getOrgRoleDisplay(cohortUser?.userOrgRole)}
                    </p>
                    )}
                </div>
                </div>
            )
            })}
        </div>
        </div>
    )
  })

  // 14 Jun 2026
//   cohortUserOptions = cohortUsers.map(cohortUser => {
//     const isChecked = formData.cohortUserIds.some(
//         item => item.userId?.toString() === cohortUser.id?.toString()
//     )

//     return (
//         <div
//         key={cohortUser.id}
//         className="flex justify-between items-center bg-gray-100 rounded m-1 p-1"
//         >
//         <div className="flex justify-start items-center">
//             <input
//             type="checkbox"
//             checked={isChecked}
//             onChange={() => handleCohortUserToggle(cohortUser.id)}
//             className="m-1"
//             />

//             <div>
//             <p className="text-xs m-1">
//                 {cohortUser?.name || cohortUser?.username || cohortUser?.email}
//             </p>

//             <p className="text-[9px] text-gray-500 mx-1">
//                 {cohortUser?.email}
//             </p>
//             </div>
//         </div>

//         {getOrgRoleDisplay(cohortUser?.userOrgRole) && (
//             <p className="text-[9px] bg-orange-100 text-orange-700 rounded-full mx-1 px-2 py-1">
//                 {getOrgRoleDisplay(cohortUser?.userOrgRole)}
//             </p>
//         )}

//         {/* {cohortUser?.userOrgRole && (
//             <p className="text-[9px] bg-orange-100 text-orange-700 rounded-full mx-1 px-2 py-1">
//             {cohortUser.userOrgRole}
//             </p>
//         )} */}
//         </div>
//     )
//   })
}

  return (
    <div className="bg-gray-200 p-1 mx-auto mt-5 rounded-lg shadow-md shadow-black">
      {isLoading && <PulseLoader color={"#ea580c"} />}

      {isError && (
        <p className="text-orange-700 font-semi-bold py-2">
          {error?.data?.message}
        </p>
      )}

      <div className="bg-white p-1 m-1 rounded">
        <p className="p-1 m-1 font-bold font-poppins text-xl">
          Add a Cohort
        </p>

        <div className="bg-gray-200 rounded m-1 p-1">
          <form onSubmit={handleSubmit} className="m-1 p-1 text-xs font-poppins">

            {/* 1 - Organization */}
            <div className="bg-white rounded m-1 p-2">
            <p className="font-bold text-xs m-1">Organization</p>

            <select
                name="orgId"
                value={formData.orgId}
                onChange={handleChange}
                className="w-full rounded p-2 m-1 bg-gray-100"
            >
                <option value="">Select Organization</option>
                {organizationOptions}
            </select>
            </div>

            {/* 2 - Trail */}
            <div className="bg-white rounded m-1 p-2">
              <p className="font-bold text-xs m-1">Trail</p>

              <select
                name="trailId"
                value={formData.trailId}
                onChange={handleChange}
                className="w-full rounded p-2 m-1 bg-gray-100"
              >
                <option value="">Select Trail</option>
                {trailOptions}
              </select>
            </div>

            {/* 3 - Cohort Basic Fields */}
            <div className="bg-white rounded m-1 p-2">
              <p className="font-bold text-xs m-1">Cohort Details</p>

              <input
                type="text"
                name="cohortName"
                placeholder="Enter the cohort name here"
                value={formData.cohortName}
                onChange={handleChange}
                className="w-full rounded p-2 m-1 bg-gray-100"
              />

              <input
                type="text"
                name="cohortCode"
                placeholder="Enter the cohort code here"
                value={formData.cohortCode}
                onChange={handleChange}
                className="w-full rounded p-2 m-1 bg-gray-100"
              />

              <input
                type="text"
                name="cohortDesc"
                placeholder="Enter the cohort description here"
                value={formData.cohortDesc}
                onChange={handleChange}
                className="w-full rounded p-2 m-1 bg-gray-100"
              />

              <input
                type="number"
                name="cohortSequence"
                placeholder="Enter display sequence"
                value={formData.cohortSequence}
                onChange={handleChange}
                className="w-full rounded p-2 m-1 bg-gray-100"
              />

              <select
                name="cohortStatus"
                value={formData.cohortStatus}
                onChange={handleChange}
                className="w-full rounded p-2 m-1 bg-gray-100"
              >
                <option value="Planned">Planned</option>
                <option value="Scheduled">Scheduled</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
                <option value="Archived">Archived</option>
              </select>
            </div>

            {/* 4 - Dates */}
            <div className="bg-white rounded m-1 p-2">
              <p className="font-bold text-xs m-1">Planned Dates</p>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <p className="text-[10px] m-1">Start Date</p>
                  <input
                    type="date"
                    name="plannedStartDate"
                    value={formData.plannedStartDate}
                    onChange={handleChange}
                    className="w-full rounded p-2 m-1 bg-gray-100"
                  />
                </div>

                <div>
                  <p className="text-[10px] m-1">End Date</p>
                  <input
                    type="date"
                    name="plannedEndDate"
                    value={formData.plannedEndDate}
                    onChange={handleChange}
                    className="w-full rounded p-2 m-1 bg-gray-100"
                  />
                </div>
              </div>
            </div>

            {/* 5 - Coaches */}
            <div className="bg-white rounded m-1 p-2">
              <p className="font-bold text-xs m-1">Coaches</p>
              <p className="text-[8px] m-1 italic text-gray-600">
                Select one or more coaches for this cohort.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                {coachOptions}
              </div>
            </div>

            {/* 6 - Sponsors */}
            <div className="bg-white rounded m-1 p-2">
              <p className="font-bold text-xs m-1">Sponsors</p>
              <p className="text-[8px] m-1 italic text-gray-600">
                Select one or more sponsors for this cohort.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                {sponsorOptions}
              </div>
            </div>

            {/* 6 - Cohort Users */}
            <div className="bg-white rounded m-1 p-2">
            <div className="flex justify-between items-center">
                <div>
                <p className="font-bold text-xs m-1">Cohort Users</p>
                <p className="text-[8px] m-1 italic text-gray-600">
                    {/* Select users from the selected organization who should belong to this cohort. */}
                    {/* Select users to move into this cohort for the selected organization and trail.
                    If a user is currently in another cohort for this trail, the backend will transfer them. */}
                    Select users to move into this cohort. Users are grouped by their current cohort for the selected organization and trail.
                </p>
                </div>

                <p className="text-[10px] bg-orange-100 text-orange-700 rounded-full mx-2 px-2 py-1">
                Selected: {formData.cohortUserIds?.length || 0}
                </p>
            </div>


            {(!formData.orgId || !formData.trailId) && (
            <div className="bg-gray-100 rounded m-1 p-2">
                <p className="text-[10px] text-gray-600">
                Select an organization and trail first to load users by current cohort.
                </p>
            </div>
            )}

            {formData.orgId && formData.trailId && (
            <div className="max-h-96 overflow-y-auto">
                {cohortUserOptions}
            </div>
            )}



            {/* {!formData.orgId && (
                <div className="bg-gray-100 rounded m-1 p-2">
                <p className="text-[10px] text-gray-600">
                    Select an organization first to load eligible users.
                </p>
                </div>
            )} */}

            {/* {formData.orgId && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1 max-h-72 overflow-y-auto">
                {cohortUserOptions}
                </div>
            )} */}
            </div>

            {/* 8 - About / Pic / Active */}
            <div className="bg-white rounded m-1 p-2">
              <input
                type="text"
                name="aboutMe"
                placeholder="Add internal notes about this cohort"
                value={formData.aboutMe}
                onChange={handleChange}
                className="w-full rounded p-2 m-1 bg-gray-100"
              />

              <input
                type="file"
                name="pic"
                onChange={handleChange}
                className="w-full rounded p-2 m-1 bg-gray-100"
              />

              <div className="flex justify-start items-center bg-gray-100 rounded m-1 p-2">
                <input
                  type="checkbox"
                  name="active"
                  checked={formData.active}
                  onChange={handleChange}
                  className="m-1"
                />
                <p className="text-xs m-1">Active</p>
              </div>
            </div>

            {/* 7 - Actions */}
            <div id="actions" className="bg-white m-1 px-1 py-3 rounded">
              <EtBtnFormBasic
                name={"Create"}
                type={"create"}
                handleClick={handleSubmit}
              />

              <EtBtnFormBasic
                name={"Clear"}
                type={"clear"}
                handleClick={handleClear}
              />

              <EtBtnFormBasic
                name={"Cancel"}
                type={"cancel"}
                handleClick={handleCancel}
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default EtCreateOrgTrailSessionCohort